import React, { useState, useEffect } from "react";
import { 
  Coins, 
  Layers, 
  Sparkles, 
  Calculator, 
  FileSpreadsheet, 
  Folder, 
  Tag, 
  Package, 
  Search, 
  Box, 
  ChevronRight, 
  ChevronLeft,
  ChevronDown,
  Menu,
  X,
  Plus,
  Settings 
} from "lucide-react";
import PrecoNovosProdutosSubTab from "./PrecoNovosProdutosSubTab";
import ModelagemSubTab from "./ModelagemSubTab";
import GeradorAnunciosSubTab from "./GeradorAnunciosSubTab";
import TaxonomiaManagerModal from "../components/modals/TaxonomiaManagerModal";
import CriarNovoProdutoModal from "../components/modals/CriarNovoProdutoModal";
import TelaProduto from "../components/product/TelaProduto";
import TelaTipoJoia from "../components/product/TelaTipoJoia";
import CatalogImporter from "../components/CatalogImporter";

const CalculadoraAvulsaSubTab = React.lazy(() => import("./CalculadoraAvulsaSubTab"));

/**
 * NovosProdutosTab.js — Módulo de Criação & Engenharia de Produtos
 *
 * Menu Lateral Responsivo:
 * - Em Mobile e Tablet (< lg / 1024px): Drawer deslizante (off-canvas com overlay) + barra contextual de topo.
 * - Em Desktop (>= lg): Barra lateral ancorada com botão para recolher (rail compacto w-20) ou expandir (w-64).
 */
export default function NovosProdutosTab({ user, hasAccess, findCatalogItem }) {
  const [activeSubTab, setActiveSubTab] = useState(() => {
    return localStorage.getItem("novos_produtos_active_subtab") || "ficha_produto";
  });
  const [selectedProductSku, setSelectedProductSku] = useState(() => {
    return localStorage.getItem("novos_produtos_selected_sku") || "DIR-AUR";
  });
  const [cadSearchSku, setCadSearchSku] = useState("");
  const [selectedTypePrefix, setSelectedTypePrefix] = useState("MMADA");
  const [customPrefixInput, setCustomPrefixInput] = useState("");
  const [isTaxonomiaOpen, setIsTaxonomiaOpen] = useState(false);
  const [isNovoProdutoModalOpen, setIsNovoProdutoModalOpen] = useState(false);

  // Controle de abertura do Drawer no mobile / tablet
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Controle de colapso no desktop (memorizado no localStorage)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem("novos_produtos_sidebar_collapsed") === "true";
  });

  useEffect(() => {
    localStorage.setItem("novos_produtos_sidebar_collapsed", isCollapsed ? "true" : "false");
  }, [isCollapsed]);

  // Fecha o drawer com a tecla Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isDrawerOpen]);

  // Save active subtab and selected SKU to localStorage
  useEffect(() => {
    localStorage.setItem("novos_produtos_active_subtab", activeSubTab);
  }, [activeSubTab]);

  useEffect(() => {
    if (selectedProductSku) {
      localStorage.setItem("novos_produtos_selected_sku", selectedProductSku);
    }
  }, [selectedProductSku]);

  const menuItems = [
    {
      id: "ficha_produto",
      label: "Ficha do Produto",
      icon: Package,
      badge: "Canônica",
      color: "text-blue-400"
    },
    {
      id: "modelagem",
      label: "Modelagem CAD",
      icon: Layers,
      badge: "3D",
      color: "text-purple-400"
    },
    {
      id: "explorar_tipos",
      label: "Explorar por Tipo / Família",
      icon: Tag,
      badge: "SKUs",
      color: "text-amber-400"
    },
    {
      id: "gerador_anuncios",
      label: "Gerador de Anúncios",
      icon: Sparkles,
      badge: "IA",
      color: "text-pink-400"
    },
    {
      id: "calc_avulsa",
      label: "Calculadora Avulsa",
      icon: Calculator,
      badge: "Orçamentos",
      color: "text-indigo-400"
    },
    {
      id: "configuracoes",
      label: "Configurações",
      icon: Settings,
      badge: "Importador",
      color: "text-slate-400"
    }
  ];

  const currentActiveItem = menuItems.find(item => item.id === activeSubTab) || menuItems[0];
  const CurrentActiveIcon = currentActiveItem.icon;

  return (
    <div className="flex flex-col lg:flex-row h-full w-full overflow-hidden bg-slate-50 relative">
      
      {/* BARRA DE NAVEGAÇÃO CONTEXTUAL MOBILE / TABLET (visível apenas em telas menores < lg) */}
      <div className="lg:hidden flex items-center justify-between px-3.5 py-2.5 bg-slate-900 text-white border-b border-slate-800 shrink-0 z-30 shadow-md">
        <button
          onClick={() => setIsDrawerOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold transition-all border border-slate-700 active:scale-95 cursor-pointer shadow-xs"
          title="Abrir navegação dos módulos"
        >
          <Menu size={16} className="text-blue-400 shrink-0" />
          <div className="flex items-center gap-1.5 min-w-0">
            <CurrentActiveIcon size={14} className={currentActiveItem.color || "text-blue-400"} />
            <span className="truncate max-w-[150px] sm:max-w-[240px]">{currentActiveItem.label}</span>
          </div>
          <ChevronDown size={14} className="text-slate-400 shrink-0" />
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setIsNovoProdutoModalOpen(true);
              setIsDrawerOpen(false);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer active:scale-95"
            title="Criar Novo Produto"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">Criar Produto</span>
          </button>

          <button
            onClick={() => {
              setIsTaxonomiaOpen(true);
              setIsDrawerOpen(false);
            }}
            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-blue-400 rounded-xl border border-slate-700 transition cursor-pointer active:scale-95"
            title="Categorias & Taxonomia"
          >
            <Folder size={16} />
          </button>
        </div>
      </div>

      {/* OVERLAY ESCURO COM DESFOQUE PARA MOBILE / TABLET */}
      {isDrawerOpen && (
        <div
          onClick={() => setIsDrawerOpen(false)}
          className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-xs lg:hidden transition-opacity"
          aria-hidden="true"
        />
      )}

      {/* MENU LATERAL (DESKTOP DOCKED OU OFF-CANVAS DRAWER EM MOBILE/TABLET) */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          ${isCollapsed ? "lg:w-20" : "lg:w-64"}
          w-72 bg-slate-900 text-white shrink-0 flex flex-col justify-between
          border-r border-slate-800 p-3 sm:p-4 shadow-2xl lg:shadow-xl
          transition-all duration-300 ease-in-out
          ${isDrawerOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div className="space-y-4 sm:space-y-6">
          {/* TÍTULO DO MÓDULO + BOTÕES DE CONTROLE (FECHAR MOBILE OU COLAPSAR DESKTOP) */}
          <div className="flex flex-col gap-3 px-1 pt-1 sm:px-2 sm:pt-2 border-b border-slate-800 pb-3 sm:pb-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                <div className="p-2 sm:p-2.5 bg-blue-600/30 border border-blue-500/30 rounded-xl text-blue-400 shrink-0">
                  <Box size={20} />
                </div>
                {!isCollapsed && (
                  <div className="min-w-0">
                    <h2 className="font-bold text-xs sm:text-sm text-white tracking-wide truncate">
                      Engenharia & Produtos
                    </h2>
                    <span className="text-[10px] text-slate-400 font-medium block truncate">
                      Módulo Canônico de Criação
                    </span>
                  </div>
                )}
              </div>

              {/* Botão Fechar no Mobile / Tablet */}
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="lg:hidden p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg cursor-pointer transition-colors"
                title="Fechar menu lateral"
              >
                <X size={18} />
              </button>

              {/* Botão Recolher/Expandir no Desktop */}
              <button
                onClick={() => setIsCollapsed(prev => !prev)}
                className="hidden lg:flex p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg cursor-pointer transition-colors"
                title={isCollapsed ? "Expandir menu lateral" : "Recolher menu lateral"}
              >
                {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              </button>
            </div>

            {/* BOTÃO DE ADICIONAR NOVO PRODUTO / SKU PAI */}
            <button
              onClick={() => {
                setIsNovoProdutoModalOpen(true);
                setIsDrawerOpen(false);
              }}
              className={`w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-xs rounded-xl shadow-lg transition-all active:scale-95 border border-blue-400/30 cursor-pointer ${
                isCollapsed ? "p-2.5" : "px-3.5 py-2.5"
              }`}
              title={isCollapsed ? "Criar Novo Produto" : undefined}
            >
              <Plus size={16} className="shrink-0" />
              {!isCollapsed && <span>+ Criar Novo Produto</span>}
            </button>
          </div>

          {/* LISTA VERTICAL DE OPÇÕES DO MENU */}
          <nav className="space-y-1.5">
            {!isCollapsed && (
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 block mb-2">
                Navegação dos Módulos
              </span>
            )}
            {menuItems.map((item) => {
              const IconComp = item.icon;
              const isActive = activeSubTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSubTab(item.id);
                    setIsDrawerOpen(false); // Fecha o drawer ao selecionar no mobile/tablet
                  }}
                  title={isCollapsed ? item.label : undefined}
                  className={`w-full flex items-center rounded-xl font-semibold text-xs transition-all text-left group cursor-pointer ${
                    isCollapsed ? "justify-center p-2.5" : "justify-between px-3.5 py-2.5"
                  } ${
                    isActive
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-900/40 font-bold"
                      : "text-slate-300 hover:bg-slate-800/80 hover:text-white"
                  }`}
                >
                  <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2.5"}`}>
                    <IconComp size={17} className={`shrink-0 ${isActive ? "text-white" : item.color}`} />
                    {!isCollapsed && <span className="truncate">{item.label}</span>}
                  </div>
                  
                  {!isCollapsed && (
                    isActive ? (
                      <ChevronRight size={14} className="text-white/80 shrink-0" />
                    ) : (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 group-hover:bg-slate-700 shrink-0">
                        {item.badge}
                      </span>
                    )
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* AÇÕES DE GESTÃO / TAXONOMIA (RODAPÉ DA SIDEBAR) */}
        <div className="pt-3 sm:pt-4 border-t border-slate-800 space-y-2">
          {!isCollapsed && (
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 block mb-1">
              Ferramentas & Taxonomia
            </span>
          )}

          <button
            onClick={() => {
              setIsTaxonomiaOpen(true);
              setIsDrawerOpen(false);
            }}
            className={`w-full flex items-center bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white rounded-xl font-bold text-xs border border-blue-500/30 transition-all shadow-sm cursor-pointer ${
              isCollapsed ? "justify-center p-2.5" : "gap-2 px-3 py-2.5"
            }`}
            title="Categorias iSet & Taxonomia"
          >
            <Folder size={15} className="text-blue-400 shrink-0" />
            {!isCollapsed && <span className="truncate">Categorias iSet & Taxonomia</span>}
          </button>
        </div>
      </aside>

      {/* CONTEÚDO DAS SUB-ABAS (ÁREA PRINCIPAL COM SCROLL ÚNICO) */}
      <main className="flex-1 overflow-y-auto min-h-0 relative bg-slate-50 w-full">
        
        {/* SUB-ABA 1: FICHA DO PRODUTO */}
        <div className={activeSubTab === "ficha_produto" ? "block" : "hidden"}>
          <TelaProduto
            skuParam={selectedProductSku}
            onNavigateToSku={(sku) => setSelectedProductSku(sku)}
            onNavigateToCad={(sku) => {
              setCadSearchSku(sku || "");
              setActiveSubTab("modelagem");
            }}
          />
        </div>

        {/* SUB-ABA 2: PRECIFICAÇÃO & BOM */}
        <div className={activeSubTab === "calc_preco" ? "block h-full" : "hidden"}>
          <PrecoNovosProdutosSubTab user={user} findCatalogItem={findCatalogItem} />
        </div>

        {/* SUB-ABA 3: MODELAGEM CAD */}
        <div className={activeSubTab === "modelagem" ? "block h-full" : "hidden"}>
          <ModelagemSubTab
            user={user}
            setActiveSubTab={setActiveSubTab}
            externalSearchSku={cadSearchSku}
            onOpenProductFicha={(sku) => {
              setSelectedProductSku(sku);
              setActiveSubTab("ficha_produto");
            }}
          />
        </div>

        {/* SUB-ABA 4: EXPLORAR POR TIPO & FAMÍLIA */}
        <div className={activeSubTab === "explorar_tipos" ? "block flex flex-col" : "hidden"}>
          <div className="bg-slate-900 text-white p-3 px-4 sm:px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0 shadow-md">
            <div className="flex items-center gap-2 text-xs">
              <Tag size={16} className="text-amber-400 shrink-0" />
              <span className="font-bold text-slate-200">Explorar por Família de SKU / Tipo:</span>
              <span className="bg-amber-500/20 text-amber-300 font-mono font-bold px-2 py-0.5 rounded border border-amber-500/30">
                {selectedTypePrefix}
              </span>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                if (customPrefixInput.trim()) {
                  setSelectedTypePrefix(customPrefixInput.trim().toUpperCase());
                }
              }}
              className="flex items-center gap-2 text-xs max-w-md w-full"
            >
              <div className="relative w-full">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={customPrefixInput}
                  onChange={(e) => setCustomPrefixInput(e.target.value)}
                  placeholder="Digitar Prefixo / Família de SKU (ex: MMADA, RIN, DIR, PIN)..."
                  className="w-full pl-9 pr-4 py-1.5 bg-slate-800 border border-slate-700 rounded-xl text-white font-mono text-xs outline-none focus:border-amber-400 uppercase"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl font-bold transition-all whitespace-nowrap shadow cursor-pointer"
              >
                Explorar
              </button>
            </form>
          </div>

          <div className="flex-1 overflow-y-auto">
            <TelaTipoJoia
              typePrefixOrId={selectedTypePrefix}
              onOpenProduct={(sku) => {
                setSelectedProductSku(sku);
                setActiveSubTab("ficha_produto");
              }}
            />
          </div>
        </div>

        {/* SUB-ABA 5: GERADOR DE ANÚNCIOS (IA) */}
        <div className={activeSubTab === "gerador_anuncios" ? "block h-full" : "hidden"}>
          <GeradorAnunciosSubTab user={user} findCatalogItem={findCatalogItem} />
        </div>

        {/* SUB-ABA 6: CALCULADORA AVULSA */}
        {activeSubTab === "calc_avulsa" && (
          <React.Suspense fallback={
            <div className="flex-1 flex items-center justify-center bg-slate-50 text-slate-500 font-bold text-xs p-6 animate-pulse">
              Carregando Calculadora de Orçamentos...
            </div>
          }>
            <CalculadoraAvulsaSubTab />
          </React.Suspense>
        )}

        {/* SUB-ABA 7: CONFIGURAÇÕES / IMPORTAÇÃO */}
        <div className={activeSubTab === "configuracoes" ? "block" : "hidden"}>
          <div className="p-6 max-w-4xl mx-auto">
            <CatalogImporter />
          </div>
        </div>

      </main>

      {/* MODAL CRIAR NOVO PRODUTO / SKU PAI */}
      {isNovoProdutoModalOpen && (
        <CriarNovoProdutoModal
          isOpen={isNovoProdutoModalOpen}
          onClose={() => setIsNovoProdutoModalOpen(false)}
          onSuccess={(newSkuPai) => {
            if (newSkuPai) {
              setSelectedProductSku(newSkuPai);
              setActiveSubTab("ficha_produto");
            }
          }}
        />
      )}

      {/* MODAL GESTOR DE TAXONOMIA */}
      {isTaxonomiaOpen && (
        <TaxonomiaManagerModal
          isOpen={isTaxonomiaOpen}
          onClose={() => setIsTaxonomiaOpen(false)}
        />
      )}

    </div>
  );
}
